/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * TypeScript/React version of ValidDocIndicator
 * Based on the original JSX version in /lego/page/browser/ValidDocIndicator.jsx
 */

import React from 'react'
import { useAuthService } from '../../hooks/useAuthService'

interface FileSystemItem {
  id: string | number
  name?: string
  type?: string
  valid?: boolean
  systemFolderType?: string
  acl?: any
  [key: string]: any // Allow additional properties
}

interface ValidDocIndicatorProps {
  fsi: FileSystemItem
  liveContentVerification?: boolean // Allow override for backward compatibility
}

/**
 * ValidDocIndicator component shows a visual indicator for document validity
 *
 * Uses the auth service to access user policies. Note that liveContentVerification
 * is actually part of settings data, not user policies, so we fallback to the prop
 * until the settings API integration is complete.
 *
 * The useAuthService hook already fetches policies via authApiService.getUserPolicies()
 */
const ValidDocIndicator: React.FC<ValidDocIndicatorProps> = ({
  fsi,
  liveContentVerification = true // Default to true for testing
}) => {
  const { policies, isPoliciesLoading } = useAuthService()

  // Get liveContentVerification from policies or fallback to prop
  // Note: The policies API might not include liveContentVerification yet, so we default to the prop
  const hasLiveContentVerification = policies?.liveContentVerification ?? liveContentVerification

  // Build the className like the original: 'valid-indicator ' + (condition ? (fsi.valid ? 'valid' : 'invalid') : '')
  const getValidityClass = (): string => {
    const baseClass = 'valid-indicator'

    if (!hasLiveContentVerification) {
      return baseClass // Just base class when verification is off
    }

    // When verification is on, add valid/invalid class
    return `${baseClass} ${fsi.valid ? 'valid' : 'invalid'}`
  }

  return (
    <span
      className={getValidityClass()}
      data-testid="valid-doc-indicator"
      title={`Document ${fsi.valid ? 'valid' : 'invalid'}`}
    />
  )
}

export default ValidDocIndicator
